// Real-time Service - WebSocket connections for Durable Objects
// Connects via /ws/* routes which go directly to Worker

import { Contraction, ChatMessage, Poll } from '../types';

// Get auth token for WebSocket authentication
function getAuthToken(): string | null {
    return localStorage.getItem('coexpect_token');
}

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

// ============ Labor Room Connection ============

interface LaborState {
    isActive: boolean;
    currentContraction: {
        id: string;
        startTime: number;
    } | null;
    history: Contraction[];
    stage: 'early' | 'active' | 'transition' | 'pushing';
    lastUpdate: number;
}

interface LaborRoomCallbacks {
    onStateChange: (state: LaborState) => void;
    onStatusChange: (status: ConnectionStatus) => void;
}

class LaborRoomConnection {
    private ws: WebSocket | null = null;
    private roomId: string;
    private callbacks: LaborRoomCallbacks;
    private reconnectTimeout: number | null = null;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 10;
    private pingInterval: number | null = null;
    private isManualClose = false;

    constructor(roomId: string, callbacks: LaborRoomCallbacks) {
        this.roomId = roomId;
        this.callbacks = callbacks;

        // Handle visibility changes (app reopen)
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }

    private handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            if (this.ws?.readyState !== WebSocket.OPEN) {
                this.connect();
            } else {
                this.requestSync();
                this.startPing(); // Resume pings when visible
            }
        } else {
            // Pause pings when hidden to reduce CPU usage
            this.stopPing();
        }
    };

    connect(): void {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.requestSync();
            return;
        }

        this.isManualClose = false;
        this.callbacks.onStatusChange('connecting');

        // Connect via /ws/* route which goes directly to Worker (bypasses Pages Functions)
        // Include auth token for server-side verification
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const token = getAuthToken();
        const wsUrl = `${protocol}//${window.location.host}/ws/labor-room?id=${this.roomId}&token=${encodeURIComponent(token || '')}`;

        try {
            this.ws = new WebSocket(wsUrl);
        } catch (error) {
            this.callbacks.onStatusChange('error');
            this.attemptReconnect();
            return;
        }

        this.ws.onopen = () => {
            this.callbacks.onStatusChange('connected');
            this.reconnectAttempts = 0;
            this.startPing();
        };

        this.ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                if (message.type === 'CONNECTED' || message.type === 'SYNC') {
                    this.callbacks.onStateChange(message.data);
                }
            } catch (error) {
                console.error('Failed to parse LaborRoom message:', error);
            }
        };

        this.ws.onclose = () => {
            this.stopPing();
            this.callbacks.onStatusChange('disconnected');
            if (!this.isManualClose) {
                this.attemptReconnect();
            }
        };

        this.ws.onerror = () => {
            this.callbacks.onStatusChange('error');
        };
    }

    private startPing(): void {
        this.pingInterval = window.setInterval(() => {
            this.send({ type: 'PING' });
        }, 30000);
    }

    private stopPing(): void {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }

    private attemptReconnect(): void {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.callbacks.onStatusChange('error');
            return;
        }

        this.reconnectAttempts++;
        const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000);

        this.reconnectTimeout = window.setTimeout(() => {
            this.connect();
        }, delay);
    }

    startTimer(): void {
        this.send({ type: 'START_TIMER' });
    }

    stopTimer(): void {
        this.send({ type: 'STOP_TIMER' });
    }

    setStage(stage: 'early' | 'active' | 'transition' | 'pushing'): void {
        this.send({ type: 'SET_STAGE', data: { stage } });
    }

    requestSync(): void {
        this.send({ type: 'SYNC' });
    }

    private send(message: any): void {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
        }
    }

    disconnect(): void {
        this.isManualClose = true;
        this.stopPing();
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        }
        this.ws?.close();
        this.ws = null;
    }
}

// ============ Community Room Connection ============

interface CommunityState {
    messages: ChatMessage[];
    activeUsers: number;
}

interface CommunityRoomCallbacks {
    onMessage: (message: ChatMessage) => void;
    onMessagesSync: (messages: ChatMessage[]) => void;
    onPollUpdate: (messageId: string, poll: Poll) => void;
    onMessageDelete: (messageId: string) => void;
    onMessageEdit: (messageId: string, newText: string, editedAt: number) => void;
    onUserCountChange: (count: number) => void;
    onStatusChange: (status: ConnectionStatus) => void;
}

class CommunityRoomConnection {
    private ws: WebSocket | null = null;
    private week: number;
    private userId: string;
    private userName: string;
    private callbacks: CommunityRoomCallbacks;
    private reconnectTimeout: number | null = null;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 10;
    private pingInterval: number | null = null;
    private isManualClose = false;

    constructor(
        week: number,
        userId: string,
        userName: string,
        callbacks: CommunityRoomCallbacks
    ) {
        this.week = week;
        this.userId = userId;
        this.userName = userName;
        this.callbacks = callbacks;

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }

    private handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            if (this.ws?.readyState !== WebSocket.OPEN) {
                this.connect();
            } else {
                this.requestSync();
                this.startPing(); // Resume pings when visible
            }
        } else {
            // Pause pings when hidden to reduce CPU usage
            this.stopPing();
        }
    };

    connect(): void {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.requestSync();
            return;
        }

        this.isManualClose = false;
        this.callbacks.onStatusChange('connecting');

        // Connect via /ws/* route which goes directly to Worker (bypasses Pages Functions)
        // This route is configured in wrangler-worker.toml to handle WebSocket on the same domain
        // Include auth token for server-side verification
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const token = getAuthToken();
        const wsUrl = `${protocol}//${window.location.host}/ws/community-room?week=${this.week}&token=${encodeURIComponent(token || '')}`;

        try {
            this.ws = new WebSocket(wsUrl);
        } catch (error) {
            this.callbacks.onStatusChange('error');
            this.attemptReconnect();
            return;
        }

        this.ws.onopen = () => {
            this.callbacks.onStatusChange('connected');
            this.reconnectAttempts = 0;
            this.startPing();
        };

        this.ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);

                switch (message.type) {
                    case 'CONNECTED':
                    case 'SYNC':
                        if (message.data?.messages) {
                            this.callbacks.onMessagesSync(message.data.messages);
                        }
                        if (message.data?.activeUsers !== undefined) {
                            this.callbacks.onUserCountChange(message.data.activeUsers);
                        }
                        break;

                    case 'SEND_MESSAGE':
                        this.callbacks.onMessage(message.data);
                        break;

                    case 'VOTE_POLL':
                        this.callbacks.onPollUpdate(message.data.messageId, message.data.poll);
                        break;

                    case 'DELETE_MESSAGE':
                        this.callbacks.onMessageDelete(message.data.messageId);
                        break;

                    case 'EDIT_MESSAGE':
                        this.callbacks.onMessageEdit(message.data.messageId, message.data.newText, message.data.editedAt);
                        break;

                    case 'USER_JOINED':
                    case 'USER_LEFT':
                        if (message.data?.activeUsers !== undefined) {
                            this.callbacks.onUserCountChange(message.data.activeUsers);
                        }
                        break;
                }
            } catch (error) {
                console.error('Failed to parse CommunityRoom message:', error);
            }
        };

        this.ws.onclose = () => {
            this.stopPing();
            this.callbacks.onStatusChange('disconnected');
            if (!this.isManualClose) {
                this.attemptReconnect();
            }
        };

        this.ws.onerror = () => {
            this.callbacks.onStatusChange('error');
        };
    }

    private startPing(): void {
        this.pingInterval = window.setInterval(() => {
            this.send({ type: 'PING' });
        }, 30000);
    }

    private stopPing(): void {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }

    private attemptReconnect(): void {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.callbacks.onStatusChange('error');
            return;
        }

        this.reconnectAttempts++;
        const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000);

        this.reconnectTimeout = window.setTimeout(() => {
            this.connect();
        }, delay);
    }

    sendMessage(text: string, isAnonymous?: boolean): void {
        this.send({ type: 'SEND_MESSAGE', data: { text, isAnonymous } });
    }

    createPoll(question: string, options: string[], isAnonymous?: boolean): void {
        this.send({ type: 'CREATE_POLL', data: { question, options, isAnonymous } });
    }

    votePoll(messageId: string, optionId: string): void {
        this.send({ type: 'VOTE_POLL', data: { messageId, optionId } });
    }

    requestSync(): void {
        this.send({ type: 'SYNC' });
    }

    deleteMessage(messageId: string): void {
        this.send({ type: 'DELETE_MESSAGE', data: { messageId } });
    }

    editMessage(messageId: string, newText: string): void {
        this.send({ type: 'EDIT_MESSAGE', data: { messageId, newText } });
    }

    private send(message: any): void {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
        }
    }

    disconnect(): void {
        this.isManualClose = true;
        this.stopPing();
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        }
        this.ws?.close();
        this.ws = null;
    }
}

// ============ Factory Functions ============

export function createLaborRoomConnection(
    roomId: string,
    callbacks: LaborRoomCallbacks
): LaborRoomConnection {
    return new LaborRoomConnection(roomId, callbacks);
}

export function createCommunityRoomConnection(
    week: number,
    userId: string,
    userName: string,
    callbacks: CommunityRoomCallbacks
): CommunityRoomConnection {
    return new CommunityRoomConnection(week, userId, userName, callbacks);
}

// ============ Partner Room Connection ============

interface PartnerState {
    nameMatches: string[];
    savedFacts: string[];
    partnerOnline: boolean;
    partnerName: string | null;
    partnerView: string | null;
}

interface PartnerRoomCallbacks {
    onStateChange: (state: PartnerState) => void;
    onPartnerJoined: (name: string) => void;
    onPartnerLeft: (name: string) => void;
    onPartnerView: (view: string | null, name: string) => void;
    onNameLiked: (name: string, by: string) => void;
    onNameMatched: (name: string) => void;
    onFactSaved: (factId: string, by: string) => void;
    onFactUnsaved: (factId: string) => void;
    onKickLogged: (data: any) => void;
    onPartnerUnlinked: () => void;
    onCheckinCompleted: (by: string, mood: string) => void;
    onProfileUpdated: (by: string, changes: string[]) => void;
    onAppointmentAdded: (by: string, title: string, date: string) => void;
    onTodoAdded: (by: string, id: string, text: string, category: string) => void;
    onTodoUpdated: (by: string, id: string, isCompleted: boolean) => void;
    onTodoDeleted: (by: string, id: string) => void;
    onDataUpdated: (dataType: string, by: string) => void;
    onStatusChange: (status: ConnectionStatus) => void;
}

class PartnerRoomConnection {
    private ws: WebSocket | null = null;
    private coupleId: string;
    private userId: string;
    private userName: string;
    private role: string;
    private callbacks: PartnerRoomCallbacks;
    private reconnectTimeout: number | null = null;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 10;
    private pingInterval: number | null = null;
    private isManualClose = false;

    constructor(
        coupleId: string,
        userId: string,
        userName: string,
        role: 'partner' | 'pregnant',
        callbacks: PartnerRoomCallbacks
    ) {
        this.coupleId = coupleId;
        this.userId = userId;
        this.userName = userName;
        this.role = role;
        this.callbacks = callbacks;

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }

    private handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            if (this.ws?.readyState !== WebSocket.OPEN) {
                this.connect();
            } else {
                this.requestSync();
                this.startPing(); // Resume pings when visible
            }
        } else {
            // Pause pings when hidden to reduce CPU usage
            this.stopPing();
        }
    };

    connect(): void {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.requestSync();
            return;
        }

        this.isManualClose = false;
        this.callbacks.onStatusChange('connecting');

        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const token = getAuthToken();
        const wsUrl = `${protocol}//${window.location.host}/ws/partner-room?coupleId=${this.coupleId}&token=${encodeURIComponent(token || '')}&role=${this.role}`;

        try {
            this.ws = new WebSocket(wsUrl);
        } catch (error) {
            this.callbacks.onStatusChange('error');
            this.attemptReconnect();
            return;
        }

        this.ws.onopen = () => {
            this.callbacks.onStatusChange('connected');
            this.reconnectAttempts = 0;
            this.startPing();
        };

        this.ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);

                switch (message.type) {
                    case 'CONNECTED':
                    case 'SYNC':
                        this.callbacks.onStateChange(message.data);
                        break;
                    case 'PARTNER_JOINED':
                        this.callbacks.onPartnerJoined(message.data.name);
                        break;
                    case 'PARTNER_LEFT':
                        this.callbacks.onPartnerLeft(message.data.name);
                        break;
                    case 'PARTNER_VIEW':
                        this.callbacks.onPartnerView(message.data.view, message.data.name);
                        break;
                    case 'NAME_LIKED':
                        this.callbacks.onNameLiked(message.data.name, message.data.by);
                        break;
                    case 'NAME_MATCHED':
                        this.callbacks.onNameMatched(message.data.name);
                        break;
                    case 'FACT_SAVED':
                        this.callbacks.onFactSaved(message.data.factId, message.data.by);
                        break;
                    case 'FACT_UNSAVED':
                        this.callbacks.onFactUnsaved(message.data.factId);
                        break;
                    case 'KICK_LOGGED':
                        this.callbacks.onKickLogged(message.data);
                        break;
                    case 'PARTNER_UNLINKED':
                        this.callbacks.onPartnerUnlinked();
                        break;
                    case 'CHECKIN_COMPLETED':
                        this.callbacks.onCheckinCompleted(message.data.by, message.data.mood);
                        break;
                    case 'PROFILE_UPDATED':
                        this.callbacks.onProfileUpdated(message.data.by, message.data.changes || []);
                        break;
                    case 'APPOINTMENT_ADDED':
                        this.callbacks.onAppointmentAdded(message.data.by, message.data.title, message.data.date);
                        break;
                    case 'TODO_ADDED':
                        this.callbacks.onTodoAdded(message.data.by, message.data.id, message.data.text, message.data.category);
                        break;
                    case 'TODO_UPDATED':
                        this.callbacks.onTodoUpdated(message.data.by, message.data.id, message.data.isCompleted);
                        break;
                    case 'TODO_DELETED':
                        this.callbacks.onTodoDeleted(message.data.by, message.data.id);
                        break;
                    case 'DATA_UPDATED':
                        this.callbacks.onDataUpdated(message.data.dataType, message.data.by);
                        break;
                }
            } catch (error) {
                console.error('Failed to parse PartnerRoom message:', error);
            }
        };

        this.ws.onclose = () => {
            this.stopPing();
            this.callbacks.onStatusChange('disconnected');
            if (!this.isManualClose) {
                this.attemptReconnect();
            }
        };

        this.ws.onerror = () => {
            this.callbacks.onStatusChange('error');
        };
    }

    private startPing(): void {
        this.pingInterval = window.setInterval(() => {
            this.send({ type: 'PING' });
        }, 30000);
    }

    private stopPing(): void {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }

    private attemptReconnect(): void {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.callbacks.onStatusChange('error');
            return;
        }

        this.reconnectAttempts++;
        const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000);

        this.reconnectTimeout = window.setTimeout(() => {
            this.connect();
        }, delay);
    }

    // Set current view for presence
    setView(view: string | null): void {
        this.send({ type: 'SET_VIEW', data: { view } });
    }

    // Notify that user liked a name
    nameLiked(name: string, partnerLikedNames: string[]): void {
        this.send({ type: 'NAME_LIKED', data: { name, partnerLikedNames } });
    }

    // Notify that user saved a fact
    factSaved(factId: string): void {
        this.send({ type: 'FACT_SAVED', data: { factId } });
    }

    // Notify that user unsaved a fact
    factUnsaved(factId: string): void {
        this.send({ type: 'FACT_UNSAVED', data: { factId } });
    }

    // Notify of kick count update
    kickLogged(kickCount: number, sessionId: string): void {
        this.send({ type: 'KICK_LOGGED', data: { kickCount, sessionId } });
    }

    // Notify partner that we're unlinking
    notifyUnlink(): void {
        this.send({ type: 'PARTNER_UNLINKED' });
    }

    // Notify partner of daily check-in completion
    notifyCheckinCompleted(mood: string): void {
        this.send({ type: 'CHECKIN_COMPLETED', data: { mood } });
    }

    // Notify partner of profile update
    notifyProfileUpdated(changes: string[]): void {
        this.send({ type: 'PROFILE_UPDATED', data: { changes } });
    }

    // Notify partner of new appointment
    notifyAppointmentAdded(title: string, date: string): void {
        this.send({ type: 'APPOINTMENT_ADDED', data: { title, date } });
    }

    // Notify partner of new todo
    notifyTodoAdded(id: string, text: string, category: string): void {
        this.send({ type: 'TODO_ADDED', data: { id, text, category } });
    }

    // Notify partner of todo update
    notifyTodoUpdated(id: string, isCompleted: boolean): void {
        this.send({ type: 'TODO_UPDATED', data: { id, isCompleted } });
    }

    // Notify partner of todo deletion
    notifyTodoDeleted(id: string): void {
        this.send({ type: 'TODO_DELETED', data: { id } });
    }

    // Notify partner that shared data was updated (birth-plan, hospital-bag, names, reminders, checklists)
    notifyDataUpdated(dataType: string): void {
        this.send({ type: 'DATA_UPDATED', data: { dataType } });
    }

    requestSync(): void {
        this.send({ type: 'SYNC' });
    }

    private send(message: any): void {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
        }
    }

    disconnect(): void {
        this.isManualClose = true;
        this.stopPing();
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        }
        this.ws?.close();
        this.ws = null;
    }
}

export function createPartnerRoomConnection(
    coupleId: string,
    userId: string,
    userName: string,
    role: 'partner' | 'pregnant',
    callbacks: PartnerRoomCallbacks
): PartnerRoomConnection {
    return new PartnerRoomConnection(coupleId, userId, userName, role, callbacks);
}

export type {
    LaborState,
    LaborRoomCallbacks,
    LaborRoomConnection,
    CommunityState,
    CommunityRoomCallbacks,
    CommunityRoomConnection,
    PartnerState,
    PartnerRoomCallbacks,
    PartnerRoomConnection,
    ConnectionStatus,
};
